import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Ban, Check, CheckCircle2, ChevronDown, ChevronRight, ExternalLink, FileUp, Pencil, Plus, RotateCcw, Send, Split, Trash2, X } from 'lucide-react';
import type {
  RfqHubFileRelease,
  RfqPackage,
  RfqProject,
  RfqSubmission,
  SupplierInvite,
  SupplierOrg,
  TimberSupplierInvite,
} from '../../types';
import type { WorkTypeVendorSummary } from '../../utils/vendorAdminProjectSummary';
import { packageHubReleaseIds } from '../../utils/rfqPackageReleases';
import { formatCncQuoteSummary, packageBoardFeet } from '../../utils/cncVendorExport';
import { cncAssignmentChipLabel, cncMaterialFlowLabel } from '../../utils/cncRfqAssignments';
import { useAuth } from '../../utils/AuthContext';
import { useCRM } from '../../utils/CRMContext';
import { getUserInitials } from '../../utils/avatarUtils';
import { canShowCombinedQuoteSplitEntry } from '../../utils/combinedQuoteSplit';
import { canApproveSupplierQuoteOnBehalf } from '../../utils/staffSupplierQuoteAccess';
import CombinedQuoteSplitModal from './CombinedQuoteSplitModal';
import './CombinedQuoteSplitModal.css';
import { supplierStructureScopeBadgeLabel } from '../../utils/timberRfqStructureScope';
import { packageScopeDiffersFromInvite } from '../../utils/rfqPackageScopeDrift';
import { isReferenceRelease } from '../../utils/rfqReleasePurpose';
import {
  cncSubmissionHistoryKey,
  groupQuoteSubmissionHistory,
  siblingSubmissionIdsInQuoteStream,
} from '../../utils/rfqSubmissionHistory';
import {
  approvalClearIdsForTimberQuote,
  timberAssignedStructuresFullyCovered,
  timberQuoteAssignedStructureIds,
  timberQuoteAssignableStructures,
  timberQuoteStructureScopeDisplayLabel,
  timberQuoteStructureScopeLabel,
} from '../../utils/timberQuoteStreamKey';
import { formatQuoteTotal, submissionQuoteTotal } from '../../utils/rfqQuoteTotal';
import { rfqSubmissionDeleteLabel } from '../../utils/otherQuoteUpload';
import {
  isLegacyPrePortalSubmission,
  isStaffManualUploadSubmission,
  staffManualUploadBadgeLabel,
} from '../../utils/rfqSubmissionUploadLabel';
import {
  filterPackagesForReleaseSelection,
  approvedSupplierOrgIdsForReleaseSelection,
  approvedSubmissionIdsForReleaseSelection,
  buildCncSupplierApprovalUpdate,
  buildTimberApprovalUpdate,
  PRE_PORTAL_RELEASE_ID,
} from '../../utils/timberRfqPrePortalVersion';
import { buildRfqSupplierSaveUpdates, supplierOrgIdsForReleaseSelection } from '../../utils/rfqProjectSuppliers';
import { rfqProjectSupplierShipTo, shipToContextForWorkType, type ProjectSiteAddressInput } from '../../utils/supplierShipTo';
import SupplierShipToLine from './SupplierShipToLine';
import CncRfqSuppliersModal from './CncRfqSuppliersModal';
import SupplierQuoteApprovalModal, { type SupplierQuoteApprovalTarget } from './SupplierQuoteApprovalModal';
import ScopeChangeDismissConfirmModal from './ScopeChangeDismissConfirmModal';
import RfqQuoteDeleteConfirmModal from './RfqQuoteDeleteConfirmModal';
import StaffSupplierQuoteUploadModal from './StaffSupplierQuoteUploadModal';
import CncSupplierInputEditModal from './CncSupplierInputEditModal';
import CncQuoteSubmissionEditModal from './CncQuoteSubmissionEditModal';
import { PmCncInviteInputAdjustedBadge, PmSubmissionQuoteInputAdjustedBadge } from './PmQuoteInputAdjustedBadge';
import { canInternalEditQuoteSubmission } from '../../utils/rfqSubmissionUploadLabel';
import {
  cncRfqAwardSubmissionKey,
  recordSupplierBillForRfqAward,
  retractSupplierBillsForRfqOrg,
} from '../../utils/supplierBillIngestion';
import SupplierPaymentTermsModal from './SupplierPaymentTermsModal';
import {
  findSupplierBillForCncOrgAward,
  findSupplierBillForRfqAward,
  setSupplierBillPaymentTerms,
} from '../../utils/supplierBillsService';
import type { SupplierPaymentTermsSpec } from '../../utils/supplierBills';
import {
  buildQuoteRejectionTargets,
  cncQuoteDeclineTarget,
  isRfqQuoteRejected,
  type QuoteRejectionTarget,
} from '../../utils/rfqQuoteRejectionTargets';
import { useSupplierBills } from '../../hooks/useSupplierBills';
import {
  primarySubmissionsFromGroups,
  resolveQuoteStreamDisplay,
  visibleQuoteRowsNewestFirst,
} from '../../utils/vendorPortalQuoteDisplay';
import { cncAwardedSubmissionIdsForPackage } from '../../utils/vendorPortalApprovals';
import {
  cncQuoteApprovedAt,
  cncQuoteApproverInitials,
  buildApprovedQuotePaymentTermsTarget,
  type ApprovedQuotePaymentTermsTarget,
  findAwardBillForCncOrg,
  findAwardBillForSubmission,
  resolveApprovedQuoteBannerMeta,
  timberQuoteApprovedAt,
  timberQuoteApproverInitials,
} from '../../utils/approvedQuoteBanner';
import { resolveCncAwardedQuote } from '../../utils/cncAwardedQuote';
import ApprovedQuoteBanner from './ApprovedQuoteBanner';
import './TimberRfqSuppliersPanel.css';

export interface CncRfqSuppliersPanelProps {
  project: RfqProject;
  projectSupplierOrgIds: string[];
  workSummary: WorkTypeVendorSummary;
  supplierOrgById: Map<string, SupplierOrg>;
  cncCapableOrgs: SupplierOrg[];
  projectSite: ProjectSiteAddressInput;
  supplierInvites: SupplierInvite[];
  projectNumber?: string;
  projectName?: string;
  packages: RfqPackage[];
  submissionsByPackageId: Map<string, RfqSubmission[]>;
  selectedReleaseId: string | null;
  hubReleases?: RfqHubFileRelease[];
  canStaffUploadQuote?: boolean;
  publishBusy?: boolean;
  onSendQuoteRequest?: (orgIds: string[]) => void;
  /** Resend after the supplier's structure scope changed on an already-published package. */
  onResendScopeChange?: (orgId: string) => void;
  /** Dismiss scope-change reminder without emailing the supplier. */
  onDismissScopeChange?: (orgId: string) => Promise<boolean>;
  /** Supplier org ids eligible for quote rejection emails (another supplier was approved). */
  rejectionTargetOrgIds?: ReadonlySet<string>;
  onRejectQuote?: (orgId: string) => void;
  onRequestRejectionNotices?: (targets: QuoteRejectionTarget[], afterClose?: () => void) => void;
  /** Timber RFQ on the same deal — job site applies when supplier is on both work types. */
  timberRfqProject?: RfqProject | null;
  hubProjectManagerUid?: string;
  onUpdateProject: (id: string, updates: Partial<RfqProject>) => Promise<void>;
  onOpenSubmission: (sub: RfqSubmission) => void;
  onDeleteSubmission?: (sub: RfqSubmission, project: RfqProject) => void | Promise<void>;
}

const CncRfqSuppliersPanel: React.FC<CncRfqSuppliersPanelProps> = ({
  project,
  projectSupplierOrgIds,
  workSummary,
  supplierOrgById,
  cncCapableOrgs,
  projectSite,
  supplierInvites,
  projectNumber,
  projectName,
  packages,
  submissionsByPackageId,
  selectedReleaseId,
  hubReleases = [],
  canStaffUploadQuote = false,
  publishBusy = false,
  onSendQuoteRequest,
  onResendScopeChange,
  onDismissScopeChange,
  rejectionTargetOrgIds,
  onRejectQuote,
  onRequestRejectionNotices,
  timberRfqProject,
  hubProjectManagerUid,
  onUpdateProject,
  onOpenSubmission,
  onDeleteSubmission,
}) => {
  const { user } = useAuth();
  const { crmUsers, rfqPackages, rfqSubmissions } = useCRM();
  const supplierBills = useSupplierBills();
  const [modalOpen, setModalOpen] = useState(false);
  const [splitTarget, setSplitTarget] = useState<{
    sub: RfqSubmission;
    orgId: string;
    orgName: string;
  } | null>(null);
  const [saveBusy, setSaveBusy] = useState(false);
  const [deleteBusyId, setDeleteBusyId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{
    sub: RfqSubmission;
    orgId: string;
    orgName: string;
  } | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [approvalTarget, setApprovalTarget] = useState<SupplierQuoteApprovalTarget | null>(null);
  const [approvalEmailOnly, setApprovalEmailOnly] = useState(false);
  const approvalFollowUpRef = useRef<(() => void) | null>(null);
  const [paymentTermsTarget, setPaymentTermsTarget] = useState<ApprovedQuotePaymentTermsTarget | null>(
    null,
  );
  const [uploadTarget, setUploadTarget] = useState<{ orgId: string; orgName: string } | null>(null);
  const [proposalStructureCount, setProposalStructureCount] = useState(0);
  const [proposalStructures, setProposalStructures] = useState<
    import('../../types').TimberSupplierStructureRef[]
  >([]);
  const [expandedHistoryOrgIds, setExpandedHistoryOrgIds] = useState<Set<string>>(new Set());
  const [scopeDismissTarget, setScopeDismissTarget] = useState<{ orgId: string; orgName: string } | null>(
    null,
  );
  const [scopeDismissBusy, setScopeDismissBusy] = useState(false);
  const [cncInputEditTarget, setCncInputEditTarget] = useState<{
    orgId: string;
    orgName: string;
    assignments: import('../../types').CncSpeciesAssignment[];
  } | null>(null);
  const [cncQuoteEditTarget, setCncQuoteEditTarget] = useState<{
    sub: RfqSubmission;
    orgId: string;
    orgName: string;
  } | null>(null);

  const canApproveSplit = useMemo(() => {
    const row = crmUsers.find((u) => u.uid === user?.id);
    return canApproveSupplierQuoteOnBehalf({
      authRole: user?.role,
      designations: row?.designations,
      hubProjectManagerUid,
      userId: user?.id,
    });
  }, [user, crmUsers, hubProjectManagerUid]);

  const timberPackages = useMemo(
    () =>
      timberRfqProject?.id
        ? rfqPackages.filter((p) => p.rfqProjectId === timberRfqProject.id)
        : [],
    [rfqPackages, timberRfqProject?.id],
  );

  const timberSubmissions = useMemo(
    () =>
      timberRfqProject?.id
        ? rfqSubmissions.filter((s) => s.rfqProjectId === timberRfqProject.id)
        : [],
    [rfqSubmissions, timberRfqProject?.id],
  );

  const toggleHistory = (orgId: string) => {
    setExpandedHistoryOrgIds(prev => {
      const next = new Set(prev);
      if (next.has(orgId)) next.delete(orgId);
      else next.add(orgId);
      return next;
    });
  };

  useEffect(() => {
    if (!project.dealId) return;
    let cancelled = false;
    void (async () => {
      try {
        const { loadDealProposalStructures } = await import('../../utils/loadDealProposalStructures');
        const structures = await loadDealProposalStructures(project.dealId, { hubProjectId: project.timberProjectId });
        if (cancelled) return;
        setProposalStructures(structures);
        setProposalStructureCount(structures.length);
      } catch {
        // Badge falls back to a plain structure count.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [project.dealId]);

  const packagesForRelease = useMemo(
    () => filterPackagesForReleaseSelection(packages, selectedReleaseId),
    [packages, selectedReleaseId],
  );

  const displaySupplierOrgIds = useMemo(
    () => supplierOrgIdsForReleaseSelection(packagesForRelease, projectSupplierOrgIds),
    [packagesForRelease, projectSupplierOrgIds],
  );

  const approvalReleaseId = selectedReleaseId ?? PRE_PORTAL_RELEASE_ID;

  const approvedOrgIdsForRelease = useMemo(
    () => approvedSupplierOrgIdsForReleaseSelection(project, selectedReleaseId),
    [project, selectedReleaseId],
  );

  const approvedSubmissionIds = useMemo(
    () =>
      new Set(
        approvedSubmissionIdsForReleaseSelection(
          project,
          packages,
          submissionsByPackageId,
          selectedReleaseId,
        ),
      ),
    [project, packages, submissionsByPackageId, selectedReleaseId],
  );

  const packageByOrgId = useMemo(() => {
    const m = new Map<string, RfqPackage>();
    packagesForRelease.forEach((p) => {
      if (p.supplierOrgId) m.set(p.supplierOrgId, p);
    });
    return m;
  }, [packagesForRelease]);

  const publishedReleaseIds = useMemo(() => {
    const ids = new Set<string>();
    packages.forEach((p) => {
      for (const id of packageHubReleaseIds(p)) ids.add(id);
    });
    return ids;
  }, [packages]);

  const selectedReleasePublished =
    !!selectedReleaseId
    && selectedReleaseId !== PRE_PORTAL_RELEASE_ID
    && publishedReleaseIds.has(selectedReleaseId);

  // Reference release selected: packages on it are production hand-offs, not quote
  // requests — no "Scope changed" nag, and the send action reads as sharing.
  const selectedReleaseIsReference = useMemo(() => {
    if (!selectedReleaseId) return false;
    const release = hubReleases.find((r) => r.id === selectedReleaseId);
    return !!release && isReferenceRelease(release);
  }, [hubReleases, selectedReleaseId]);

  const handleSaveSuppliers = async (updates: {
    supplierOrgIds: string[];
    supplierOrgId: string;
    supplierOrgName?: string;
    timberSupplierInvites: TimberSupplierInvite[];
    approvedSupplierOrgId?: string;
  }) => {
    if (!project.id) return;
    setSaveBusy(true);
    try {
      await onUpdateProject(project.id, buildRfqSupplierSaveUpdates(updates) as Partial<RfqProject>);
    } finally {
      setSaveBusy(false);
    }
  };

  const requestDeleteSubmission = (sub: RfqSubmission, orgId: string, orgName: string) => {
    if (!sub.id || !onDeleteSubmission) return;
    setDeleteError(null);
    setDeleteTarget({ sub, orgId, orgName });
  };

  const handleDeleteSubmission = async () => {
    const sub = deleteTarget?.sub;
    const orgId = deleteTarget?.orgId;
    if (!sub?.id || !orgId || !onDeleteSubmission) return;
    setDeleteBusyId(sub.id);
    setDeleteError(null);
    try {
      const pkg = packageByOrgId.get(orgId);
      const subs = (pkg?.id && submissionsByPackageId.get(pkg.id)) || [];
      const quoteSubs = subs.filter(s => !s.notBidding && (s.storagePath || s.cncQuote));
      const inferredAwarded =
        approvedOrgIdsForRelease.includes(orgId)
          ? resolveCncAwardedQuote({
              quotes: quoteSubs,
              approvedAt: cncQuoteApprovedAt(project, selectedReleaseId, orgId),
            })
          : null;
      const wasExplicitlyApproved = approvedSubmissionIds.has(sub.id);
      const wasLegacyAwarded = !!inferredAwarded?.id && inferredAwarded.id === sub.id && !wasExplicitlyApproved;
      const wasApproved = wasExplicitlyApproved || wasLegacyAwarded;

      await onDeleteSubmission(sub, project);

      if (wasApproved && project.id) {
        const updates: Partial<RfqProject> = wasExplicitlyApproved
          ? buildTimberApprovalUpdate(project, approvalReleaseId, sub.id, false)
          : {};
        const remainingExplicit = quoteSubs.filter(
          (s) =>
            !!s.id
            && s.id !== sub.id
            && (
              approvedSubmissionIds.has(s.id)
              || (updates.approvedSubmissionIds ?? project.approvedSubmissionIds ?? []).includes(s.id)
            ),
        );
        const clearOrg =
          approvedOrgIdsForRelease.includes(orgId)
          && (wasLegacyAwarded || remainingExplicit.length === 0);
        if (clearOrg) {
          Object.assign(
            updates,
            buildCncSupplierApprovalUpdate(
              { ...project, ...updates },
              approvalReleaseId,
              orgId,
              false,
            ),
          );
          void retractSupplierBillsForRfqOrg({
            project,
            orgId,
            byUserId: user?.id || '',
            byUserName: user?.name || user?.email || '',
          });
        }
        if (Object.keys(updates).length) {
          await onUpdateProject(project.id, updates);
        }
      }
      setDeleteTarget(null);
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'Could not delete this quote.');
    } finally {
      setDeleteBusyId(null);
    }
  };

  return (
    <div className="srfq-timber-suppliers-panel">
      {displaySupplierOrgIds.length === 0 ? (
        <p className="srfq-muted srfq-timber-suppliers-empty">No suppliers invited yet.</p>
      ) : (
        <ul className="srfq-timber-supplier-cards">
          {displaySupplierOrgIds.map((orgId) => {
            const onCurrentRoster = projectSupplierOrgIds.includes(orgId);
            const orgName = supplierOrgById.get(orgId)?.name || orgId;
            const invite = project.timberSupplierInvites?.find(row => row.supplierOrgId === orgId);
            const speciesAssignments = invite?.cncSpeciesAssignments ?? [];
            const pkg = packageByOrgId.get(orgId);
            const structureBadge = supplierStructureScopeBadgeLabel(
              invite,
              proposalStructureCount,
              pkg,
            );
            const isOrgApproved = approvedOrgIdsForRelease.includes(orgId);
            const subs = (pkg?.id && submissionsByPackageId.get(pkg.id)) || [];
            const latestSub = subs[0];
            const quoteSubs = subs.filter((s) => !s.notBidding && (s.storagePath || s.cncQuote));
            const quoteGroups = groupQuoteSubmissionHistory(quoteSubs, cncSubmissionHistoryKey);
            const packageAwardedIds = pkg
              ? cncAwardedSubmissionIdsForPackage(project, pkg, orgId, quoteSubs)
              : new Set<string>();
            const streamApprovedIds = new Set<string>([
              ...Array.from(approvedSubmissionIds),
              ...Array.from(packageAwardedIds),
            ]);
            const latestQuoteSubs = primarySubmissionsFromGroups(quoteGroups, streamApprovedIds);
            const quoteEntries = quoteGroups.flatMap((group) =>
              visibleQuoteRowsNewestFirst(
                resolveQuoteStreamDisplay(group, streamApprovedIds),
                streamApprovedIds,
              ),
            );
            const latestQuote = latestQuoteSubs[0] ?? null;
            const hasQuote = quoteGroups.length > 0;
            const combinedTotal = latestQuoteSubs.reduce((sum, sub) => {
              const t = submissionQuoteTotal(sub);
              return t != null ? sum + t : sum;
            }, 0);
            const approvedCount = latestQuoteSubs.filter(
              (s) => s.id && streamApprovedIds.has(s.id),
            ).length;
            const assignedStructureIds = timberQuoteAssignedStructureIds(
              project,
              orgId,
              pkg,
              proposalStructures,
            );
            const approvedPrimaries = latestQuoteSubs.filter(
              (s) => s.id && streamApprovedIds.has(s.id),
            );
            const allQuotesApproved = timberAssignedStructuresFullyCovered({
              approvedSubs: approvedPrimaries,
              assignedStructureIds,
              primaryStreamCount: latestQuoteSubs.length,
            });
            const hasPendingRevisions = quoteEntries.some((entry) => entry.isPendingRevision);
            const cardFullyApproved =
              isOrgApproved && allQuotesApproved && !hasPendingRevisions && hasQuote;
            const hasPartialApproval =
              (isOrgApproved || approvedCount > 0) && !cardFullyApproved && approvedCount > 0;
            const isRejected = quoteEntries.some(
              (e) => !e.isPendingRevision && isRfqQuoteRejected(e.sub),
            );

            const needsQuoteRequest =
              onCurrentRoster
              && !pkg
              && selectedReleasePublished
              && !!onSendQuoteRequest;
            const scopeChanged =
              onCurrentRoster
              && !!pkg
              && !pkg.legacyImport
              && !isReferenceRelease(pkg)
              && !selectedReleaseIsReference
              && selectedReleasePublished
              && (!!onResendScopeChange || !!onDismissScopeChange)
              && packageScopeDiffersFromInvite(pkg, invite, proposalStructureCount);
            const canRejectQuote =
              (!!onRequestRejectionNotices || !!onRejectQuote)
              && user?.role !== 'staff'
              && !isRejected
              && !isOrgApproved
              && quoteEntries.some((e) => !!e.sub.id)
              && (
                quoteEntries.some((e) => isLegacyPrePortalSubmission(e.sub))
                || !!rejectionTargetOrgIds?.has(orgId)
              );
            const showQuoteActions =
              hasQuote ||
              latestSub?.notBidding ||
              (!isOrgApproved && hasQuote && !!project.id);

            const awardBill = findAwardBillForCncOrg(supplierBills, project.id, orgId);

            return (
              <li
                key={orgId}
                className={`srfq-timber-supplier-card srfq-cnc-supplier-card ${
                  cardFullyApproved
                    ? 'approved'
                    : hasPartialApproval
                      ? 'approved-partial'
                      : hasQuote
                        ? 'quoted'
                        : ''
                }${!onCurrentRoster ? ' srfq-timber-supplier-card--prior-release' : ''}`}
              >
                <div className="srfq-timber-supplier-card-head">
                  <div className="srfq-timber-supplier-card-head-main">
                    <div className="srfq-timber-supplier-card-title">
                      {cardFullyApproved && <Check size={12} aria-hidden />}
                      <span className="srfq-timber-supplier-card-name">{orgName}</span>
                      {structureBadge && (
                        <span
                          className="srfq-timber-supplier-structures-badge"
                          title={(invite?.structures ?? []).map(s => s.name).join(', ')}
                        >
                          {structureBadge}
                        </span>
                      )}
                    </div>
                    <div className="srfq-timber-supplier-card-statuses">
                      {cardFullyApproved ? (
                        <span className="srfq-timber-supplier-status approved">Approved</span>
                      ) : isRejected ? (
                        <span className="srfq-timber-supplier-status rejected">Rejected</span>
                      ) : hasPartialApproval && hasPendingRevisions ? (
                        <>
                          <span className="srfq-timber-supplier-status approved">Approved</span>
                          <span className="srfq-timber-supplier-status revision-pending">Revision pending</span>
                        </>
                      ) : approvedCount > 0 ? (
                        <span className="srfq-timber-supplier-status quoted">
                          {approvedCount}/{latestQuoteSubs.length} approved
                        </span>
                      ) : hasQuote ? (
                        <span className="srfq-timber-supplier-status quoted">Quoted</span>
                      ) : pkg && isReferenceRelease(pkg) ? (
                        <span
                          className="srfq-timber-supplier-status published"
                          title="Reference release shared for production — this supplier is not asked to requote."
                        >
                          Reference shared
                        </span>
                      ) : pkg ? (
                        <span className="srfq-timber-supplier-status published">Published</span>
                      ) : !onCurrentRoster ? (
                        <span className="srfq-timber-supplier-status prior-release">Prior release</span>
                      ) : needsQuoteRequest ? (
                        <span className="srfq-timber-supplier-status invited">
                          {selectedReleaseIsReference ? 'Not shared' : 'Not sent'}
                        </span>
                      ) : null}
                      {scopeChanged && (
                        <span
                          className="srfq-timber-supplier-status scope-changed"
                          title="This supplier's structure selection no longer matches their published quote request"
                        >
                          Scope changed
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="srfq-timber-supplier-card-toolbar">
                    {needsQuoteRequest && (
                      <button
                        type="button"
                        className="srfq-timber-supplier-send-rfq"
                        disabled={publishBusy}
                        onClick={() => onSendQuoteRequest?.([orgId])}
                        title={
                          selectedReleaseIsReference
                            ? 'Share the refined model for production. No quote is requested.'
                            : undefined
                        }
                      >
                        <Send size={12} aria-hidden />
                        {selectedReleaseIsReference ? 'Share reference release' : 'Send quote request'}
                      </button>
                    )}
                    {scopeChanged && (
                      <div className="srfq-scope-change-actions">
                        {onResendScopeChange && (
                          <button
                            type="button"
                            className="srfq-timber-supplier-send-rfq srfq-timber-supplier-resend-scope"
                            disabled={publishBusy}
                            onClick={() => onResendScopeChange(orgId)}
                          >
                            <Send size={12} aria-hidden />
                            Resend — scope changed
                          </button>
                        )}
                        {onDismissScopeChange && (
                          <button
                            type="button"
                            className="srfq-scope-dismiss-btn"
                            disabled={publishBusy || scopeDismissBusy}
                            aria-label={`Dismiss scope change reminder for ${orgName}`}
                            title="Dismiss reminder"
                            onClick={() => setScopeDismissTarget({ orgId, orgName })}
                          >
                            <X size={12} aria-hidden />
                          </button>
                        )}
                      </div>
                    )}
                    {canStaffUploadQuote && project.id && (
                      <button
                        type="button"
                        className="srfq-staff-upload-quote-btn srfq-staff-upload-quote-btn--inline"
                        onClick={() => setUploadTarget({ orgId, orgName })}
                      >
                        <FileUp size={12} aria-hidden />
                        Upload quote PDF
                      </button>
                    )}
                    {(() => {
                      if (!timberRfqProject?.id || hasQuote) return null;
                      const timberSubsForOrg = timberSubmissions.filter(
                        (s) => s.supplierOrgId === orgId && !s.notBidding && (!!s.storagePath || !!s.cncQuote),
                      );
                      const timberSub =
                        timberSubsForOrg.find((s) => !s.linkedCncSubmissionId && !isRfqQuoteRejected(s))
                        ?? null;
                      if (
                        !timberSub
                        || !canShowCombinedQuoteSplitEntry({
                          canApprove: canApproveSplit,
                          orgId,
                          org: supplierOrgById.get(orgId),
                          timberProject: timberRfqProject,
                          cncProject: project,
                          timberSubmission: timberSub,
                          cncSubmissionsForOrg: quoteSubs,
                        })
                      ) {
                        return null;
                      }
                      return (
                        <button
                          type="button"
                          className="cqs-split-cue-btn"
                          title="Split CNC dollars out of the combined timber quote"
                          onClick={() =>
                            setSplitTarget({
                              sub: timberSub,
                              orgId,
                              orgName,
                            })
                          }
                        >
                          <Split size={12} aria-hidden />
                          Split CNC from timber quote
                        </button>
                      );
                    })()}
                  </div>
                </div>

                <SupplierShipToLine
                  address={rfqProjectSupplierShipTo(project, orgId, {
                    ...shipToContextForWorkType('cnc_quote', timberRfqProject, project),
                    orgId,
                    projectSite,
                  })}
                />

                {speciesAssignments.length > 0 && (
                  <div className="srfq-cnc-supplier-assignments">
                    <div className="srfq-cnc-supplier-assignment-chips">
                      {invite && <PmCncInviteInputAdjustedBadge invite={invite} />}
                      {speciesAssignments.map((a) => (
                        <span key={a.species} className="srfq-timber-supplier-structures-badge">
                          {cncAssignmentChipLabel(a)}
                        </span>
                      ))}
                      {canStaffUploadQuote && project.id && (
                        <button
                          type="button"
                          className="srfq-cnc-input-edit-btn"
                          onClick={() =>
                            setCncInputEditTarget({
                              orgId,
                              orgName,
                              assignments: speciesAssignments,
                            })
                          }
                        >
                          <Pencil size={12} aria-hidden />
                          Edit input
                        </button>
                      )}
                    </div>
                    <ul className="srfq-cnc-supplier-material-flow">
                      {speciesAssignments.map((a) => (
                        <li key={a.species}>
                          {cncMaterialFlowLabel(a, 'staff')}
                          {a.note ? ` — ${a.note}` : ''}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {hasQuote && combinedTotal > 0 && quoteEntries.length > 1 && (
                  <p className="srfq-timber-supplier-quote-total">
                    {formatQuoteTotal(combinedTotal)} total
                  </p>
                )}

                {showQuoteActions && (
                  <div className="srfq-cnc-supplier-card-body">
                    {!hasQuote && latestSub?.notBidding && (
                      <div className="srfq-cnc-quote-row">
                        <div className="srfq-cnc-quote-row-content">
                          <div className="srfq-cnc-quote-row-main">
                            <span className="srfq-muted">Not bidding</span>
                          </div>
                        </div>
                      </div>
                    )}
                    {quoteEntries.map(({
                      sub,
                      isPendingRevision,
                      previous,
                      showPreviousHistory,
                      canonicalApprovedId,
                    }) => {
                      const isQuoteApproved =
                        !isPendingRevision
                        && !isRfqQuoteRejected(sub)
                        && !!sub.id
                        && sub.id === canonicalApprovedId;
                      const quoteSummary =
                        sub.cncQuote && pkg
                          ? formatCncQuoteSummary(sub.cncQuote, packageBoardFeet(pkg))
                          : null;
                      const quoteTotalLabel = formatQuoteTotal(submissionQuoteTotal(sub));
                      const canEditQuoteInput = canInternalEditQuoteSubmission(
                        sub,
                        pkg?.id,
                        canStaffUploadQuote,
                      );
                      const quotedUnderPreviousScope =
                        pkg?.scopeRevisedAt instanceof Date
                        && sub.createdAt instanceof Date
                        && sub.createdAt.getTime() < pkg.scopeRevisedAt.getTime();
                      // Multi-quote CNC: never show another quote's / org bill on this row.
                      const ownBill = findAwardBillForSubmission(
                        supplierBills,
                        project.id,
                        sub.id,
                      );
                      const rowBill =
                        ownBill
                        || (isQuoteApproved && approvedCount < 2 ? awardBill : null);
                      const noBillYet =
                        isQuoteApproved
                        && !!sub.id
                        && !ownBill
                        && approvedCount >= 2;
                      const historyId = `${orgId}:${sub.id ?? sub.storagePath ?? ''}`;
                      const historyOpen = expandedHistoryOrgIds.has(historyId);
                      return (
                        <div
                          key={sub.id ?? sub.storagePath}
                          data-rfq-submission-id={sub.id || undefined}
                          className={`srfq-cnc-quote-row${isQuoteApproved ? ' approved' : ''}${
                            isPendingRevision ? ' pending-revision' : ''
                          }`}
                        >
                          {isPendingRevision && (
                            <div className="srfq-timber-quote-row-banner srfq-timber-quote-row-banner--pending">
                              Revised quote — review &amp; approve
                            </div>
                          )}
                          {isQuoteApproved && (
                            <ApprovedQuoteBanner
                              meta={resolveApprovedQuoteBannerMeta({
                                approvedAt:
                                  timberQuoteApprovedAt(project, sub.id)
                                  ?? cncQuoteApprovedAt(project, selectedReleaseId, orgId),
                                initials:
                                  timberQuoteApproverInitials(project, sub.id)
                                  ?? cncQuoteApproverInitials(project, selectedReleaseId, orgId),
                                bill: rowBill,
                                dealId: project.dealId,
                              })}
                              noBillYet={noBillYet}
                              onSendApprovalEmail={
                                canApproveSplit && project.id
                                  ? () => {
                                      const total = formatQuoteTotal(submissionQuoteTotal(sub));
                                      const summary = sub.cncQuote
                                        ? formatCncQuoteSummary(sub.cncQuote, pkg ? packageBoardFeet(pkg) : project.boardFeet ?? 0)
                                        : '';
                                      const scope = timberQuoteStructureScopeDisplayLabel(
                                        sub,
                                        timberQuoteAssignableStructures(project, orgId, pkg),
                                        proposalStructureCount,
                                      );
                                      const quoteLabel =
                                        total !== '—'
                                          ? summary
                                            ? `${summary} — ${total}`
                                            : `${scope} — ${total}`
                                          : summary || scope || 'CNC quote';
                                      approvalFollowUpRef.current = null;
                                      setApprovalEmailOnly(true);
                                      setApprovalTarget({
                                        kind: 'cnc',
                                        orgId,
                                        orgName,
                                        sub,
                                        quoteLabel,
                                      });
                                    }
                                  : undefined
                              }
                              onEditPaymentTerms={
                                canApproveSplit && project.id
                                  ? () => {
                                      const total = formatQuoteTotal(submissionQuoteTotal(sub));
                                      const quoteLabel =
                                        total !== '—'
                                          ? `CNC quote — ${orgName} — ${total}`
                                          : `CNC quote — ${orgName}`;
                                      setPaymentTermsTarget(
                                        buildApprovedQuotePaymentTermsTarget({
                                          submissionKey: sub.id ?? cncRfqAwardSubmissionKey(orgId),
                                          orgId,
                                          vendorName: orgName,
                                          quoteLabel,
                                          quoteAmount: submissionQuoteTotal(sub),
                                          bill: rowBill,
                                        }),
                                      );
                                    }
                                  : undefined
                              }
                            />
                          )}
                          <div className="srfq-cnc-quote-row-content">
                            <div className="srfq-cnc-quote-row-main">
                              <span className="srfq-timber-quote-structure-scope">
                                {timberQuoteStructureScopeDisplayLabel(
                                  sub,
                                  timberQuoteAssignableStructures(project, orgId, pkg),
                                  proposalStructureCount,
                                )}
                              </span>
                              <PmSubmissionQuoteInputAdjustedBadge submission={sub} />
                              {isStaffManualUploadSubmission(sub) && (
                                <span className="srfq-manual-upload-badge srfq-manual-upload-badge--inline">
                                  {staffManualUploadBadgeLabel(sub)}
                                </span>
                              )}
                              {isLegacyPrePortalSubmission(sub) && (
                                <span className="srfq-legacy-import-badge srfq-legacy-import-badge--inline">
                                  Pre-portal
                                </span>
                              )}
                              {quotedUnderPreviousScope && (
                                <span
                                  className="srfq-timber-quote-prev-scope-badge"
                                  title="Submitted before the structure scope was updated — the price may not match the current scope"
                                >
                                  Quoted under previous scope
                                </span>
                              )}
                              {quoteTotalLabel && quoteTotalLabel !== '—' && (
                                <span className="srfq-cnc-quote-total">{quoteTotalLabel}</span>
                              )}
                              {quoteSummary && (
                                <span className="srfq-cnc-supplier-quote-summary">{quoteSummary}</span>
                              )}
                            </div>
                            <div className="srfq-timber-quote-row-actions">
                              {canEditQuoteInput && (
                                <button
                                  type="button"
                                  className="srfq-quote-action-btn srfq-quote-action-btn--edit"
                                  aria-label="Edit quote input"
                                  title="Edit quote input"
                                  onClick={() =>
                                    setCncQuoteEditTarget({ sub, orgId, orgName })
                                  }
                                >
                                  <Pencil size={14} aria-hidden />
                                </button>
                              )}
                              {sub.storagePath && (
                                <button
                                  type="button"
                                  className="srfq-quote-action-btn srfq-quote-action-btn--open"
                                  aria-label="Open quote PDF"
                                  title="Open PDF in viewer"
                                  onClick={() => onOpenSubmission(sub)}
                                >
                                  <ExternalLink size={14} aria-hidden />
                                </button>
                              )}
                              {(!isQuoteApproved || isPendingRevision) && !isRfqQuoteRejected(sub) && canApproveSplit && project.id && (
                                <button
                                  type="button"
                                  className="srfq-quote-action-btn srfq-quote-action-btn--approve"
                                  aria-label={`Approve quote from ${orgName}`}
                                  title="Approve quote"
                                  onClick={() => {
                                    const total = formatQuoteTotal(submissionQuoteTotal(sub));
                                    const summary = sub.cncQuote
                                      ? formatCncQuoteSummary(sub.cncQuote, pkg ? packageBoardFeet(pkg) : project.boardFeet ?? 0)
                                      : '';
                                    const scope = timberQuoteStructureScopeDisplayLabel(
                                      sub,
                                      timberQuoteAssignableStructures(project, orgId, pkg),
                                      proposalStructureCount,
                                    );
                                    const quoteLabel =
                                      total !== '—'
                                        ? summary
                                          ? `${summary} — ${total}`
                                          : `${scope} — ${total}`
                                        : summary || scope || 'CNC quote';
                                    approvalFollowUpRef.current = null;
                                    setApprovalEmailOnly(false);
                                    setApprovalTarget({
                                      kind: 'cnc',
                                      orgId,
                                      orgName,
                                      sub,
                                      quoteLabel,
                                    });
                                  }}
                                >
                                  <CheckCircle2 size={14} aria-hidden />
                                </button>
                              )}
                              {isQuoteApproved && !isPendingRevision && canApproveSplit && project.id && sub.id && (
                                <button
                                  type="button"
                                  className="srfq-quote-action-btn srfq-quote-action-btn--unapprove"
                                  aria-label={`Undo approval for ${orgName}`}
                                  title="Undo approval"
                                  onClick={() => {
                                    const subClear = buildTimberApprovalUpdate(
                                      project,
                                      approvalReleaseId,
                                      sub.id!,
                                      false,
                                    );
                                    const remaining = latestQuoteSubs.filter(
                                      (s) => s.id && s.id !== sub.id && streamApprovedIds.has(s.id),
                                    );
                                    const updates: Partial<typeof project> = { ...subClear };
                                    if (remaining.length === 0 && isOrgApproved) {
                                      Object.assign(
                                        updates,
                                        buildCncSupplierApprovalUpdate(
                                          { ...project, ...subClear },
                                          approvalReleaseId,
                                          orgId,
                                          false,
                                        ),
                                      );
                                      void retractSupplierBillsForRfqOrg({
                                        project,
                                        orgId,
                                        byUserId: user?.id || '',
                                        byUserName: user?.name || user?.email || '',
                                      });
                                    }
                                    void onUpdateProject(project.id!, updates);
                                  }}
                                >
                                  <RotateCcw size={14} aria-hidden />
                                </button>
                              )}
                              {canRejectQuote && !isQuoteApproved && (
                                <button
                                  type="button"
                                  className="srfq-quote-action-btn srfq-quote-action-btn--reject srfq-quote-action-btn--reject-label"
                                  aria-label={`Decline quote from ${orgName}`}
                                  title="Decline quote"
                                  onClick={() => {
                                    if (onRequestRejectionNotices) {
                                      onRequestRejectionNotices([
                                        cncQuoteDeclineTarget(
                                          sub,
                                          orgId,
                                          orgName,
                                          pkg,
                                          pkg ? packageBoardFeet(pkg) : project.boardFeet ?? 0,
                                        ),
                                      ]);
                                      return;
                                    }
                                    onRejectQuote?.(orgId);
                                  }}
                                >
                                  <Ban size={14} aria-hidden />
                                  <span>Decline</span>
                                </button>
                              )}
                              {onDeleteSubmission && sub.id && (
                                <button
                                  type="button"
                                  className="srfq-quote-action-btn srfq-quote-action-btn--delete"
                                  disabled={deleteBusyId === sub.id}
                                  aria-label={`Delete quote from ${orgName}`}
                                  title="Delete quote"
                                  onClick={() => requestDeleteSubmission(sub, orgId, orgName)}
                                >
                                  <Trash2 size={14} aria-hidden />
                                </button>
                              )}
                            </div>
                          </div>
                          {showPreviousHistory && previous.length > 0 && (
                            <div className="srfq-quote-history">
                              <button
                                type="button"
                                className="srfq-quote-history-toggle"
                                aria-expanded={historyOpen}
                                onClick={() => toggleHistory(historyId)}
                              >
                                {historyOpen ? (
                                  <ChevronDown size={12} aria-hidden />
                                ) : (
                                  <ChevronRight size={12} aria-hidden />
                                )}
                                {previous.length === 1
                                  ? '1 previous quote'
                                  : `${previous.length} previous quotes`}
                              </button>
                              {historyOpen && (
                                <ul
                                  className="srfq-quote-history-list"
                                  aria-label={`Previous quotes from ${orgName}`}
                                >
                                  {previous.map(prev => {
                                    const prevTotal = formatQuoteTotal(submissionQuoteTotal(prev));
                                    const prevSummary =
                                      prev.cncQuote && pkg
                                        ? formatCncQuoteSummary(prev.cncQuote, packageBoardFeet(pkg))
                                        : null;
                                    return (
                                      <li key={prev.id ?? prev.storagePath} className="srfq-quote-history-row">
                                        <span className="srfq-timber-quote-structure-scope">
                                          {timberQuoteStructureScopeDisplayLabel(
                                            prev,
                                            timberQuoteAssignableStructures(project, orgId, pkg),
                                            proposalStructureCount,
                                          )}
                                        </span>
                                        {prevTotal !== '—' && (
                                          <span className="srfq-quote-history-price">{prevTotal}</span>
                                        )}
                                        {prevSummary && (
                                          <span className="srfq-cnc-supplier-quote-summary">{prevSummary}</span>
                                        )}
                                        {prev.createdAt instanceof Date && (
                                          <span className="srfq-quote-history-date">
                                            {prev.createdAt.toLocaleDateString(undefined, {
                                              month: 'short',
                                              day: 'numeric',
                                              year: 'numeric',
                                            })}
                                          </span>
                                        )}
                                        {(prev.storagePath || (onDeleteSubmission && prev.id)) && (
                                          <span className="srfq-quote-history-actions">
                                            {prev.storagePath && (
                                              <button
                                                type="button"
                                                className="srfq-quote-action-btn srfq-quote-action-btn--open"
                                                aria-label="Open previous quote PDF"
                                                title="Open PDF"
                                                onClick={() => onOpenSubmission(prev)}
                                              >
                                                <ExternalLink size={14} aria-hidden />
                                              </button>
                                            )}
                                            {onDeleteSubmission && prev.id && (
                                              <button
                                                type="button"
                                                className="srfq-quote-action-btn srfq-quote-action-btn--delete"
                                                disabled={deleteBusyId === prev.id}
                                                aria-label="Delete previous quote"
                                                title="Delete quote"
                                                onClick={() => requestDeleteSubmission(prev, orgId, orgName)}
                                              >
                                                <Trash2 size={14} aria-hidden />
                                              </button>
                                            )}
                                          </span>
                                        )}
                                      </li>
                                    );
                                  })}
                                </ul>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

              </li>
            );
          })}
        </ul>
      )}

      <button
        type="button"
        className="ops-hide-completed-btn srfq-timber-suppliers-manage"
        disabled={saveBusy || !project.id || cncCapableOrgs.length === 0}
        onClick={() => setModalOpen(true)}
      >
        <Plus size={13} aria-hidden />
        {projectSupplierOrgIds.length ? 'Manage suppliers…' : 'Add suppliers…'}
      </button>

      {modalOpen && project.id && (
        <CncRfqSuppliersModal
          project={project}
          projectSupplierOrgIds={projectSupplierOrgIds}
          cncCapableOrgs={cncCapableOrgs}
          projectSite={projectSite}
          timberRfqProject={timberRfqProject}
          supplierOrgById={supplierOrgById}
          onClose={() => setModalOpen(false)}
          onSave={handleSaveSuppliers}
        />
      )}

      {splitTarget && project.id && timberRfqProject?.id && (
        <CombinedQuoteSplitModal
          open
          timberSubmission={splitTarget.sub}
          timberProject={timberRfqProject}
          cncProject={project}
          orgId={splitTarget.orgId}
          orgName={splitTarget.orgName}
          packages={timberPackages}
          submissionsByPackageId={new Map(
            timberPackages
              .filter((p) => p.id)
              .map((p) => [
                p.id!,
                timberSubmissions.filter((s) => s.rfqPackageId === p.id),
              ]),
          )}
          selectedTimberReleaseId={null}
          cncPackagesForOrg={packages.filter((p) => p.supplierOrgId === splitTarget.orgId)}
          cncSubmissionsForOrg={rfqSubmissions.filter(
            (s) => s.rfqProjectId === project.id && s.supplierOrgId === splitTarget.orgId,
          )}
          hubProjectManagerUid={hubProjectManagerUid}
          onClose={() => setSplitTarget(null)}
        />
      )}

      <SupplierQuoteApprovalModal
        open={!!approvalTarget}
        target={approvalTarget}
        project={project}
        workType="cnc_quote"
        projectNumber={projectNumber}
        projectName={projectName}
        supplierOrgById={supplierOrgById}
        emailOnly={approvalEmailOnly}
        onClose={() => {
          setApprovalTarget(null);
          setApprovalEmailOnly(false);
        }}
        onFinished={() => {
          const next = approvalFollowUpRef.current;
          approvalFollowUpRef.current = null;
          next?.();
        }}
        onApproved={async () => {
          if (!approvalTarget || !project.id) return;
          const approverInitials = getUserInitials(user?.name || user?.email || '');
          const sub = approvalTarget.sub;
          const pkg =
            (sub?.rfqPackageId && packages.find((p) => p.id === sub.rfqPackageId))
            || packages.find((p) => p.supplierOrgId === approvalTarget.orgId);
          const pkgSubs =
            (sub?.rfqPackageId && submissionsByPackageId.get(sub.rfqPackageId))
            || (pkg?.id ? submissionsByPackageId.get(pkg.id) : undefined)
            || [];
          const activeSubs = pkgSubs.filter((s) => !s.notBidding && (s.storagePath || s.cncQuote));
          const orgUpdate = buildCncSupplierApprovalUpdate(
            project,
            approvalReleaseId,
            approvalTarget.orgId,
            true,
            approverInitials,
          );
          let awardUpdate: Partial<RfqProject> = { ...orgUpdate };
          if (sub?.id) {
            const assignedStructureIds = timberQuoteAssignedStructureIds(
              project,
              approvalTarget.orgId,
              pkg,
              proposalStructures,
            );
            const streamSiblings = siblingSubmissionIdsInQuoteStream(
              sub,
              activeSubs,
              cncSubmissionHistoryKey,
            );
            const overlapClear = approvalClearIdsForTimberQuote({
              approving: sub,
              candidates: activeSubs,
              assignedStructureIds,
            });
            const clearSubmissionIds = Array.from(new Set([...streamSiblings, ...overlapClear]));
            const subUpdate = buildTimberApprovalUpdate(
              { ...project, ...orgUpdate },
              approvalReleaseId,
              sub.id,
              true,
              approverInitials,
              { clearSubmissionIds },
            );
            awardUpdate = { ...orgUpdate, ...subUpdate };
          }
          await onUpdateProject(project.id, awardUpdate);
          try {
            const submissionKey = sub?.id
              ? sub.id
              : cncRfqAwardSubmissionKey(approvalTarget.orgId);
            const amount = sub ? submissionQuoteTotal(sub) : null;
            await recordSupplierBillForRfqAward(
              {
                project,
                category: 'cnc',
                orgId: approvalTarget.orgId,
                orgName: approvalTarget.orgName,
                submissionKey,
                amount,
                description: `CNC quote — ${approvalTarget.quoteLabel}`,
                byUserId: user?.id || '',
                byUserName: user?.name || user?.email || '',
              },
              { throwOnError: true },
            );
            const pendingTerms = {
              submissionKey,
              orgId: approvalTarget.orgId,
              vendorName: approvalTarget.orgName,
              quoteLabel: approvalTarget.quoteLabel,
              amount: amount ?? 0,
            };
            const nextProject = { ...project, ...awardUpdate };
            const rejectionTargets = buildQuoteRejectionTargets({
              workType: 'cnc_quote',
              project: nextProject,
              projectSupplierOrgIds,
              packages,
              submissionsByPackageId,
              selectedReleaseId: approvalReleaseId,
              supplierOrgById,
            });
            const openTerms = () => setPaymentTermsTarget(pendingTerms);
            approvalFollowUpRef.current = () => {
              if (rejectionTargets.length && onRequestRejectionNotices) {
                onRequestRejectionNotices(rejectionTargets, openTerms);
              } else {
                openTerms();
              }
            };
          } catch (err) {
            window.alert(
              err instanceof Error
                ? err.message
                : 'Quote was approved but the Accounts Payable bill could not be created. It may appear after sync.',
            );
          }
        }}
      />

      <SupplierPaymentTermsModal
        open={!!paymentTermsTarget}
        vendorName={paymentTermsTarget?.vendorName ?? ''}
        quoteLabel={paymentTermsTarget?.quoteLabel ?? ''}
        billAmount={paymentTermsTarget?.amount ?? 0}
        projectId={project.timberProjectId || undefined}
        billCategory="cnc"
        initialTerms={paymentTermsTarget?.initialTerms ?? null}
        lockedInstallmentIds={paymentTermsTarget?.lockedInstallmentIds}
        paidInstallmentIds={paymentTermsTarget?.paidInstallmentIds}
        onSave={async (terms: SupplierPaymentTermsSpec) => {
          if (!paymentTermsTarget || !project.id) return;
          const submissionKey = paymentTermsTarget.submissionKey;
          const isOrgKey = submissionKey.startsWith('org:');
          let bill = await findSupplierBillForRfqAward(project.id, submissionKey);
          // Real quote id: never fall back to another quote / supplier bill — create this quote's bill first.
          if (!bill && !isOrgKey && paymentTermsTarget.orgId) {
            await recordSupplierBillForRfqAward(
              {
                project,
                category: 'cnc',
                orgId: paymentTermsTarget.orgId,
                orgName: paymentTermsTarget.vendorName,
                submissionKey,
                amount: paymentTermsTarget.amount,
                description: paymentTermsTarget.quoteLabel || `CNC quote — ${paymentTermsTarget.vendorName}`,
                byUserId: user?.id || '',
                byUserName: user?.name || user?.email || '',
              },
              { throwOnError: true },
            );
            bill = await findSupplierBillForRfqAward(project.id, submissionKey);
          } else if (!bill && isOrgKey && paymentTermsTarget.orgId) {
            bill = await findSupplierBillForCncOrgAward(project.id, paymentTermsTarget.orgId);
          }
          if (!bill?.id) {
            throw new Error('Bill not found yet — set terms from Billing & Payables once it appears.');
          }
          await setSupplierBillPaymentTerms(bill.id, terms, {
            expectedRfqSubmissionId: isOrgKey ? undefined : submissionKey,
            allowRaiseBillAmount: true,
            byUserId: user?.id || '',
            byUserName: user?.name || user?.email || '',
          });
        }}
        onClose={() => setPaymentTermsTarget(null)}
      />

      {cncInputEditTarget && project.id && (
        <CncSupplierInputEditModal
          open
          project={project}
          orgId={cncInputEditTarget.orgId}
          orgName={cncInputEditTarget.orgName}
          assignments={cncInputEditTarget.assignments}
          onUpdateProject={onUpdateProject}
          onClose={() => setCncInputEditTarget(null)}
        />
      )}

      {cncQuoteEditTarget && (
        <CncQuoteSubmissionEditModal
          open
          submission={cncQuoteEditTarget.sub}
          orgName={cncQuoteEditTarget.orgName}
          project={project}
          orgId={cncQuoteEditTarget.orgId}
          projectPackages={packages}
          selectedReleaseId={selectedReleaseId}
          fallbackStructures={proposalStructures}
          onClose={() => setCncQuoteEditTarget(null)}
        />
      )}

      {uploadTarget && project.id && (
        <StaffSupplierQuoteUploadModal
          open
          project={project}
          supplierOrgId={uploadTarget.orgId}
          supplierName={uploadTarget.orgName}
          workType="cnc_quote"
          selectedReleaseId={selectedReleaseId}
          hubReleases={hubReleases}
          projectPackages={packages}
          fallbackStructures={proposalStructures}
          dealStructureCount={proposalStructureCount}
          onClose={() => setUploadTarget(null)}
        />
      )}

      <RfqQuoteDeleteConfirmModal
        open={!!deleteTarget}
        target={
          deleteTarget
            ? {
                quoteLabel: rfqSubmissionDeleteLabel(deleteTarget.sub),
                vendorName: deleteTarget.orgName,
                amount: submissionQuoteTotal(deleteTarget.sub),
              }
            : null
        }
        busy={!!deleteBusyId}
        error={deleteError}
        onClose={() => {
          if (!deleteBusyId) {
            setDeleteTarget(null);
            setDeleteError(null);
          }
        }}
        onConfirm={() => void handleDeleteSubmission()}
      />

      <ScopeChangeDismissConfirmModal
        open={!!scopeDismissTarget}
        supplierName={scopeDismissTarget?.orgName ?? ''}
        busy={scopeDismissBusy}
        onClose={() => {
          if (!scopeDismissBusy) setScopeDismissTarget(null);
        }}
        onConfirm={async () => {
          if (!scopeDismissTarget || !onDismissScopeChange) return;
          setScopeDismissBusy(true);
          try {
            const ok = await onDismissScopeChange(scopeDismissTarget.orgId);
            if (ok) setScopeDismissTarget(null);
          } finally {
            setScopeDismissBusy(false);
          }
        }}
      />
    </div>
  );
};

export default CncRfqSuppliersPanel;
